/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Troubleshoot Identity Resolution", description = "Diagnose and resolve common Identity Resolution issues, including graph run failures, merge quality issues, and SQL errors when querying IDR output tables.", lastUpdatedDate = new Date(1770681600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Troubleshoot Identity Resolution",
        description: "Diagnose and resolve common Identity Resolution issues, including graph run failures, merge quality issues, and SQL errors when querying IDR output tables.",
        lastUpdatedDate: "Tue Feb 10 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Quick triage checklist",
          "depth": 0,
          "id": "quick-triage-checklist"
        }, {
          "value": "Common IDR issues",
          "depth": 0,
          "id": "common-idr-issues"
        }, {
          "value": "Graph run fails",
          "depth": 1,
          "id": "graph-run-fails"
        }, {
          "value": "Too many separate profiles (under-merging)",
          "depth": 1,
          "id": "too-many-separate-profiles-under-merging"
        }, {
          "value": "Over-merged profiles or superclusters",
          "depth": 1,
          "id": "over-merged-profiles-or-superclusters"
        }, {
          "value": "Large <prefix>_unresolved table",
          "depth": 1,
          "id": "large-prefix_unresolved-table"
        }, {
          "value": "Golden Record values look wrong",
          "depth": 1,
          "id": "golden-record-values-look-wrong"
        }, {
          "value": "SQL troubleshooting for IDR tables",
          "depth": 0,
          "id": "sql-troubleshooting-for-idr-tables"
        }, {
          "value": "When to contact support",
          "depth": 0,
          "id": "when-to-contact-support"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      p: "p",
      h2: "h2",
      ol: "ol",
      li: "li",
      ul: "ul",
      code: "code",
      h3: "h3",
      a: "a"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Data and analytics engineers, platform admins"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Prerequisites"
              })
            }), _jsx(_components.td, {
              children: _jsxs("ul", {
                children: [_jsx("li", {
                  children: "An existing identity graph"
                }), _jsx("li", {
                  children: "At least one completed or failed graph run"
                })]
              })
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: "Use this page to diagnose common Identity Resolution (IDR) issues and choose the right fix path quickly."
      }), "\n", _jsx(_components.h2, {
        id: "quick-triage-checklist",
        children: "Quick triage checklist"
      }), "\n", _jsx(_components.p, {
        children: "When a run fails or results look wrong:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "Copy the full run error from IDR run details."
        }), "\n", _jsx(_components.li, {
          children: "Confirm source models still exist and are accessible to the Hightouch role."
        }), "\n", _jsxs(_components.li, {
          children: ["Verify each input model has:", "\n", _jsxs(_components.ul, {
            children: ["\n", _jsx(_components.li, {
              children: "a stable, unique primary key"
            }), "\n", _jsxs(_components.li, {
              children: ["a populated timestamp or ", _jsx(_components.code, {
                children: "updated_at"
              }), " column"]
            }), "\n"]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["Check IDR output tables in warehouse:", "\n", _jsxs(_components.ul, {
            children: ["\n", _jsx(_components.li, {
              children: _jsx(_components.code, {
                children: "<prefix>_resolved"
              })
            }), "\n", _jsx(_components.li, {
              children: _jsx(_components.code, {
                children: "<prefix>_resolved_identifiers"
              })
            }), "\n", _jsx(_components.li, {
              children: _jsx(_components.code, {
                children: "<prefix>_unresolved"
              })
            }), "\n", _jsxs(_components.li, {
              children: [_jsx(_components.code, {
                children: "<prefix>_golden_records"
              }), " (if enabled)"]
            }), "\n"]
          }), "\n"]
        }), "\n", _jsx(_components.li, {
          children: "Reproduce failing SQL in your warehouse under the same role/context as Hightouch."
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "common-idr-issues",
        children: "Common IDR issues"
      }), "\n", _jsx(_components.h3, {
        id: "graph-run-fails",
        children: "Graph run fails"
      }), "\n", _jsx(_components.p, {
        children: "Common causes:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Missing/renamed source object"
        }), "\n", _jsx(_components.li, {
          children: "Warehouse permission changes"
        }), "\n", _jsx(_components.li, {
          children: "Non-unique primary keys in input models"
        }), "\n", _jsxs(_components.li, {
          children: ["Missing or null timestamp/", _jsx(_components.code, {
            children: "updated_at"
          }), " fields"]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "What to check:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "Run details error text in Hightouch."
        }), "\n", _jsx(_components.li, {
          children: "Warehouse object existence and role permissions."
        }), "\n", _jsx(_components.li, {
          children: "Primary key uniqueness and timestamp completeness in input models."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "How to fix:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Repair source objects/permissions."
        }), "\n", _jsx(_components.li, {
          children: "Correct PK and timestamp issues upstream."
        }), "\n", _jsx(_components.li, {
          children: "Re-run graph after warehouse queries succeed under the Hightouch role."
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "too-many-separate-profiles-under-merging",
        children: "Too many separate profiles (under-merging)"
      }), "\n", _jsx(_components.p, {
        children: "Common causes:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Low identifier coverage (for example, missing email/phone on many rows)"
        }), "\n", _jsx(_components.li, {
          children: "Deterministic rules too strict"
        }), "\n", _jsx(_components.li, {
          children: "Limit rules too restrictive"
        }), "\n", _jsx(_components.li, {
          children: "Probabilistic matching not enabled where needed"
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "How to fix:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "Improve identifier coverage upstream."
        }), "\n", _jsx(_components.li, {
          children: "Revisit deterministic rule order (strongest identifiers first)."
        }), "\n", _jsx(_components.li, {
          children: "Loosen limit rules where legitimate multi-value behavior exists."
        }), "\n", _jsx(_components.li, {
          children: "Enable probabilistic matching for messy/offline data if appropriate."
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "over-merged-profiles-or-superclusters",
        children: "Over-merged profiles or superclusters"
      }), "\n", _jsx(_components.p, {
        children: "Common causes:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Weak identifiers placed too high in rule priority"
        }), "\n", _jsx(_components.li, {
          children: "Missing/loose limit rules"
        }), "\n", _jsx(_components.li, {
          children: "Probabilistic settings too permissive"
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "How to fix:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Tighten limits (for example, ", _jsx(_components.code, {
            children: "max 1 user_id per profile"
          }), ")."]
        }), "\n", _jsx(_components.li, {
          children: "Demote/remove weak bridge identifiers."
        }), "\n", _jsx(_components.li, {
          children: "Increase probabilistic confidence threshold for sensitive use cases."
        }), "\n", _jsxs(_components.li, {
          children: ["Use ", _jsx(_components.a, {
            href: "/identity-resolution/supercluster",
            children: "Supercluster detection"
          }), " review and reprocessing workflow."]
        }), "\n"]
      }), "\n", _jsxs(_components.h3, {
        id: "large-prefix_unresolved-table",
        children: ["Large ", _jsx(_components.code, {
          children: "<prefix>_unresolved"
        }), " table"]
      }), "\n", _jsx(_components.p, {
        children: "Common causes:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Duplicate primary keys"
        }), "\n", _jsx(_components.li, {
          children: "Missing required fields (especially PK/timestamp)"
        }), "\n", _jsx(_components.li, {
          children: "Upstream data quality issues"
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "How to fix:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "Group unresolved rows by source and root-cause patterns."
        }), "\n", _jsx(_components.li, {
          children: "Fix upstream ETL/model logic to enforce one row per entity and required fields."
        }), "\n", _jsx(_components.li, {
          children: "Re-run and compare unresolved volume trend."
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "golden-record-values-look-wrong",
        children: "Golden Record values look wrong"
      }), "\n", _jsx(_components.p, {
        children: "Common causes:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Survivorship strategy mismatched with business intent"
        }), "\n", _jsx(_components.li, {
          children: "Source-priority ordering does not reflect trusted systems"
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "How to fix:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "Inspect profile-level source values vs selected Golden Record value."
        }), "\n", _jsx(_components.li, {
          children: "Update survivorship rules (recency, frequency, source-priority) per field."
        }), "\n", _jsx(_components.li, {
          children: "Consider multiple graphs if different regions/brands need different logic."
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "sql-troubleshooting-for-idr-tables",
        children: "SQL troubleshooting for IDR tables"
      }), "\n", _jsx(_components.p, {
        children: "You can also see SQL compilation errors when building models, audiences, journeys, or syncs on top of IDR output tables."
      }), "\n", _jsx(_components.p, {
        children: "Typical patterns:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "Object 'DB.SCHEMA.OBJECT' does not exist or not authorized"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "invalid identifier ..."
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "[UNRESOLVED_COLUMN.WITH_SUGGESTION] ... cannot be resolved"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "ambiguous column name ..."
          })
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Fast checks for IDR-specific SQL issues:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "Confirm the IDR output table exists in the expected schema/prefix."
        }), "\n", _jsx(_components.li, {
          children: "Confirm the querying role has required access."
        }), "\n", _jsx(_components.li, {
          children: "Validate referenced columns still exist in the selected output table/view."
        }), "\n", _jsx(_components.li, {
          children: "Re-run SQL in warehouse with the same role/context as Hightouch."
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["For full SQL error patterns and fixes, see ", _jsx(_components.a, {
          href: "/models/sql-compilation-errors",
          children: "Resolve SQL compilation errors"
        }), "."]
      }), "\n", _jsx(_components.h2, {
        id: "when-to-contact-support",
        children: "When to contact support"
      }), "\n", _jsx(_components.p, {
        children: "Include:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Graph ID and run ID"
        }), "\n", _jsx(_components.li, {
          children: "Full error text"
        }), "\n", _jsx(_components.li, {
          children: "Query and result from warehouse reproduction under the Hightouch role"
        }), "\n", _jsx(_components.li, {
          children: "Affected model/table names and expected behavior"
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
